import React, { useEffect, useMemo, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { Box } from '@mui/material';
import OverflowingTooltipText from 'components/OverflowingTooltipText';
import { adminTypes } from 'helper/constant';
import { checkAdminAccess } from 'helper/utility';
import PropTypes from 'prop-types';
import routeEndpoints from 'route/endpoints';
import { getWorkflowDetailPath } from 'views/V2/Medallion/helper/constants';

import { useFetchAllWorkflowsQuery } from 'redux/slices/workflowApi';

import styles from '../sidebar.module.css';

import { getActiveItemId, sortDefaultFirst } from '../helper/utilities';

function WorkflowsSubNav({ adminType }) {
    const { pathname } = useLocation();
    const scrollRef = useRef(null);
    const { data, isLoading, isError, isFetching, refetch } = useFetchAllWorkflowsQuery();
    const workflows = useMemo(() => sortDefaultFirst(data?.workflows ?? [], 'is_default'), [data]);
    const canCreate = checkAdminAccess(adminType, [
        adminTypes.COMPANY_OWNER,
        adminTypes.COMPANY_ADMIN,
    ]);
    const showCreate = !isLoading && !isError && workflows.length === 0 && canCreate;
    const createPath = `${routeEndpoints.workflows}/new`;

    // The edit page belongs to the same workflow as its detail page.
    const currentPath = pathname.replace(
        `${routeEndpoints.workflows}/edit/`,
        `${routeEndpoints.workflows}/`
    );
    const listedPaths = workflows.map((workflow) => getWorkflowDetailPath(workflow.nanoid));
    if (showCreate) {
        listedPaths.push(createPath);
    }
    const onWorkflows = getActiveItemId(pathname) === 'workflows';
    const activePath = listedPaths.includes(currentPath) ? currentPath : routeEndpoints.workflows;

    // Keep the open workflow visible when it sits below the fold of the list.
    useEffect(() => {
        scrollRef.current
            ?.querySelector('[aria-current="page"]')
            ?.scrollIntoView({ block: 'nearest' });
    }, [activePath, workflows]);

    const linkProps = (path) => ({
        to: path,
        isActive: () => onWorkflows && path === activePath,
        className: styles.subNavItem,
        activeClassName: styles.subNavItemActive,
    });

    return (
        <Box className={styles.subNav}>
            <NavLink {...linkProps(routeEndpoints.workflows)}>All workflows</NavLink>
            {isLoading && (
                <Box className={styles.subNavStatus} role="status" aria-label="Loading workflows">
                    <span className={styles.subNavSkeleton} />
                    <span className={styles.subNavSkeleton} />
                    <span className={styles.subNavSkeleton} />
                </Box>
            )}
            {isError && !isLoading && (
                <Box className={styles.subNavError}>
                    <span>Couldn't load workflows</span>
                    <button
                        type="button"
                        className={styles.retryButton}
                        onClick={refetch}
                        disabled={isFetching}
                    >
                        Retry
                    </button>
                </Box>
            )}
            {workflows.length > 0 && (
                <Box ref={scrollRef} className={styles.subNavScroll}>
                    {workflows.map((workflow) => (
                        <NavLink
                            key={workflow.nanoid}
                            {...linkProps(getWorkflowDetailPath(workflow.nanoid))}
                        >
                            <OverflowingTooltipText
                                title={workflow.name}
                                className={styles.subNavLabel}
                                placement="right"
                            />
                            {workflow.is_default && (
                                <span className={styles.defaultTag}>Default</span>
                            )}
                        </NavLink>
                    ))}
                </Box>
            )}
            {showCreate && <NavLink {...linkProps(createPath)}>Create workflow</NavLink>}
        </Box>
    );
}

WorkflowsSubNav.propTypes = {
    adminType: PropTypes.string,
};

export default WorkflowsSubNav;
